/**
 * Client for the LogoNuri account-link routes (LN-2). Same transport as the MCP
 * API keys: `authFetch` carries the browser session, so a link is created by a
 * signed-in person and the LogoNuri bearer never enters this browser at all.
 *
 * TWO 503s, AND THEY MEAN OPPOSITE THINGS:
 *   - `logonuri_disabled`     — deliberate. The feature is dark on this
 *                               deployment. Latch it and hide the surface.
 *   - `logonuri_unconfigured` — a deployment bug (missing/malformed secret).
 *                               Do NOT latch: show "temporarily unavailable"
 *                               and let the user retry, or an operator error
 *                               looks identical to an intentional switch-off
 *                               and hides for weeks.
 *
 * `pro_required` IS ALSO NOT LATCHED. LogoNuri re-checks the tier on every
 * single call, so a user can link while Pro and lapse afterwards — and an
 * upgrade restores access with no reconnect. It is a live, recoverable
 * condition, not a setup failure.
 *
 * No call here ever needs `suppressUnauthorizedHandler`. LN-2 deliberately maps
 * an upstream 401 to 409 `logonuri_link_expired`, so a 401 from any of these
 * routes means the PageStitch session really is dead and authed-fetch.js's
 * global handler is right to fire.
 */

import { authFetch } from './auth-api.js';

/** null = unknown, true/false = known. Latched only by `logonuri_disabled`. */
let _available = null;

export function logonuriAvailability() {
  return _available;
}

/** Test seam: forget the latch between cases. */
export function resetLogoNuriAvailability() {
  _available = null;
}

export class LogoNuriError extends Error {
  constructor(message, { status, code, upgradeUrl } = {}) {
    super(message);
    this.name = 'LogoNuriError';
    this.status = status;
    this.code = code;
    this.upgradeUrl = upgradeUrl;
  }
}

async function parse(resp) {
  let data = null;
  try {
    data = await resp.json();
  } catch {
    // non-JSON body: fall through to the status-based error below
  }

  if (resp.status === 503 && data?.code === 'logonuri_disabled') {
    _available = false;
    throw new LogoNuriError('The LogoNuri integration is not enabled.', {
      status: 503,
      code: 'logonuri_disabled'
    });
  }
  if (!resp.ok) {
    throw new LogoNuriError(data?.error || `Request failed (${resp.status})`, {
      status: resp.status,
      code: data?.code,
      upgradeUrl: data?.upgradeUrl
    });
  }
  _available = true;
  return data;
}

/** @returns {Promise<{connected:boolean, connection:object|null, reason?:string}>} */
export async function getLogoNuriStatus({ refresh = false } = {}) {
  const qs = refresh ? '?refresh=1' : '';
  const resp = await authFetch(`/api/account/logonuri/status${qs}`, { method: 'GET' });
  const data = await parse(resp);
  return {
    connected: !!data.connected,
    connection: data.connection || null,
    reason: data.reason
  };
}

/** @returns {Promise<{connection:object}>} */
export async function connectLogoNuri(code) {
  const resp = await authFetch('/api/account/logonuri/connect', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code })
  });
  return parse(resp);
}

/** @returns {Promise<{removed:boolean}>} */
export async function disconnectLogoNuri() {
  const resp = await authFetch('/api/account/logonuri/connect', { method: 'DELETE' });
  return parse(resp);
}

/** @returns {Promise<{kits:Array, fetchedAt:number, cached:boolean}>} */
export async function listLogoNuriKits({ refresh = false } = {}) {
  const qs = refresh ? '?refresh=1' : '';
  const resp = await authFetch(`/api/account/logonuri/kits${qs}`, { method: 'GET' });
  const data = await parse(resp);
  return { kits: data.kits || [], fetchedAt: data.fetchedAt, cached: !!data.cached };
}

/**
 * One kit asset's bytes, as a File ready for `store.assets.store(...)`.
 *
 * Goes through PageStitch's proxy rather than logonuri.com directly, so the
 * bytes are authorized against the caller's own kit list. Thumbnails may use
 * the public URL; anything that lands in a project must come through here.
 */
export async function fetchLogoNuriAsset(slug, publicKey, { revision = null, signal } = {}) {
  const qs = revision ? `?rev=${encodeURIComponent(revision)}` : '';
  const resp = await authFetch(
    `/api/account/logonuri/kits/${encodeURIComponent(slug)}/asset/${encodeURIComponent(publicKey)}${qs}`,
    { method: 'GET', signal }
  );
  if (!resp.ok) {
    // Same taxonomy as the JSON routes — the body is JSON on every error path.
    await parse(resp);
  }
  _available = true;
  const blob = await resp.blob();
  const type = resp.headers.get('Content-Type') || blob.type || 'application/octet-stream';
  return new File([blob], filenameFor(publicKey, type), { type });
}

/** A stable, human-meaningful filename for a downloaded kit asset. */
function filenameFor(publicKey, contentType) {
  const ext = contentType.includes('svg')
    ? 'svg'
    : contentType.includes('png')
      ? 'png'
      : contentType.includes('webp')
        ? 'webp'
        : contentType.includes('jpeg')
          ? 'jpg'
          : 'bin';
  return `${publicKey}.${ext}`;
}

/**
 * Where the user goes to generate a code.
 *
 * `?connect=pagestitch` is what makes the handoff painless: LogoNuri reads it
 * and lands the user straight on the code (LN-8). Until that ships the param is
 * inert and the user navigates to the connect UI themselves.
 */
export const LOGONURI_ORIGIN = 'https://logonuri.com';
export const LOGONURI_CONNECT_URL = `${LOGONURI_ORIGIN}/app/?connect=pagestitch`;
export const LOGONURI_UPGRADE_URL = `${LOGONURI_ORIGIN}/#pricing`;

/**
 * Origins a connect-code postMessage may arrive from.
 *
 * An explicit allowlist, never `event.origin.endsWith(...)` — that matches
 * `evil-logonuri.com`. The loopback entry is for driving the flow against a
 * local LogoNuri during development.
 */
export const LOGONURI_MESSAGE_ORIGINS = Object.freeze([
  LOGONURI_ORIGIN,
  'http://localhost:8788',
  'http://127.0.0.1:8788'
]);
